import { useMemo, useState } from 'react'
import { api, session } from '../api.js'
import { RankedBar, SAMPLE_FLOOR } from '../charts.jsx'
import {
  Banner, Button, Card, ErrorState, Field, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Crane safety inspections: the register, the downloads, and the rounds the safety department schedules.
 *
 * **Two things live on one page because they answer one question.** "Is this crane's inspection up to date?"
 * needs the schedule and the history side by side — a page of schedules with no evidence of what was found, or
 * a register with no idea what was due, each leaves the reader to join them by hand.
 *
 * **Authoring is SAFETY_OFFICER *or* ADMIN**, unlike planned maintenance, which is ADMIN-only. That is the
 * requirement as stated: PM belongs to the engineering departments and inspection belongs to safety, so the
 * department that does the work is the one that plans it. An admin is included because a supervisor entering a
 * paper round is a real case.
 *
 * The report download is deliberately **not** a report-catalogue entry. `ReportController` refuses OPERATOR and
 * VENDOR at class level, and the whole point of this feature is that every role can read an inspection — so it
 * has its own endpoint, gated `isAuthenticated()`.
 */
export default function Inspections() {
  const inspections = useApi('/inspections')
  const schedules = useApi('/inspection-schedules')
  const assets = useApi('/assets')
  const due = useApi('/inspection-schedules/due?withinDays=14')
  // Aggregated server-side: the register omits item rows to stay one query, so counting failures per section
  // in the browser would mean fetching every inspection in full.
  const findings = useApi('/inspections/findings-by-section')

  const [editing, setEditing] = useState(null)   // a schedule row, or 'new'
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [open, setOpen] = useState(null)          // an inspection being read in detail

  const rows = useMemo(() => (Array.isArray(inspections.data) ? inspections.data : []), [inspections.data])
  const plans = useMemo(() => (Array.isArray(schedules.data) ? schedules.data : []), [schedules.data])
  const fleet = useMemo(() => (Array.isArray(assets.data) ? assets.data : []), [assets.data])
  const dueRows = useMemo(() => (Array.isArray(due.data) ? due.data : []), [due.data])

  async function run(fn, text) {
    setBusy(true)
    setNotice(null)
    try {
      await fn()
      // Reload rather than patch locally: the server owns nextDueAt, and after an interval change the
      // recomputed date is the point of the edit. Guessing it here would show a date nothing agrees with.
      schedules.reload()
      due.reload()
      setNotice({ kind: 'success', text })
      setEditing(null)
    } catch (e) {
      setNotice({ kind: 'danger', text: e.message })
    } finally {
      setBusy(false)
    }
  }

  const activePlans = plans.filter((s) => s.active !== false)
  const withCritical = rows.filter((r) => r.criticalNotOkCount > 0)

  // Layout only; the server is the authority. Drawing the button for a role the endpoint refuses would offer a
  // control that lands on a 403 — the same mistake M11 found on the Android side.
  const maySchedule = session.role === 'SAFETY_OFFICER' || session.role === 'ADMIN'

  return (
    <>
      <PageHeader
        title="Safety inspections"
        subtitle="The Safety Engg. Dept round, digitised. Conducted on the app; scheduled and read here."
        actions={maySchedule ? <Button onClick={() => setEditing('new')}>Schedule a round</Button> : null}
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {schedules.data && plans.length === 0 && (
        <Banner kind="warning">
          <span>
            No inspection rounds are scheduled, so <strong>nothing is ever due</strong> and no crane will be
            flagged for inspection. Inspections can still be recorded on the app at any time — a schedule is
            what makes an <em>overdue</em> one visible.
          </span>
        </Banner>
      )}

      {(inspections.loading || schedules.loading) && <Loading rows={5} />}
      {inspections.error && <ErrorState error={inspections.error} onRetry={inspections.reload} />}

      {inspections.data && (
        <MetricRow>
          <Metric label="Inspections recorded" value={rows.length} />
          <Metric
            label="With critical findings"
            value={withCritical.length}
            kind={withCritical.length ? 'danger' : 'success'}
            hint="A failed hook, rope, brake, limit or interlock"
          />
          <Metric label="Rounds scheduled" value={activePlans.length} kind={activePlans.length ? undefined : 'warning'} />
          <Metric
            label="Due within 14 days"
            value={dueRows.length}
            kind={dueRows.some((d) => d.overdue) ? 'danger' : undefined}
            hint={dueRows.filter((d) => d.overdue).length ? `${dueRows.filter((d) => d.overdue).length} overdue` : undefined}
          />
          <Metric
            label="Cranes never inspected"
            value={fleet.filter((a) => a.status !== 'DECOMMISSIONED' && !rows.some((r) => r.assetId === a.id)).length}
            hint="No record in the register at all"
            kind="warning"
          />
        </MetricRow>
      )}

      {/*
        Where on the crane the findings concentrate — DESIGN.md §11.1, a ranked horizontal bar.

        The register cannot answer this by being read: twelve sections and 59 parameters per inspection produce
        a list nobody totals by eye. Horizontal because the labels are phrases ("Neutral Interlock System"),
        which rotate illegibly under vertical bars, and capped at eight with an explicit "+N more" rather than a
        silent truncation (CLAUDE.md rule 30).

        Ranked by failure count with critical failures called out in the note, because a section is not urgent in
        proportion to its size — two failures on hooks outrank six on cabin housekeeping.
      */}
      {findings.data && findings.data.length > 0 && (
        <>
          <SectionHeader hint="Ranked by how many parameters failed">Where findings concentrate</SectionHeader>
          <Card>
            <RankedBar
              title="NOT OK by section of the form"
              hint="Across every inspection recorded"
              rows={findings.data.map((f) => ({
                label: f.section,
                value: f.notOk,
                hint: 'Parameters NOT OK',
                // Any critical failure in a section colours it as safety rather than as an ordinary defect —
                // the same convention the register's chips use.
                token: f.critical > 0 ? 'danger' : 'steel',
              }))}
              topN={8}
              sampleSize={findings.data.reduce((a, f) => a + f.notOk, 0)}
              sampleNote={findingsNote(findings.data)}
              empty="No failures recorded in any section"
            />
          </Card>
        </>
      )}

      {/* ---- Scheduled rounds ---- */}
      <SectionHeader hint={maySchedule ? 'Click a round to correct or retire it' : 'Scheduled by the safety department'}>
        Scheduled rounds
      </SectionHeader>
      <Card>
        <Table
          keyOf={(s) => s.id}
          // Not clickable for a reader who cannot edit: opening a form whose Save is refused is worse than not
          // offering the row.
          onRowClick={maySchedule ? (s) => setEditing(s) : undefined}
          columns={[
            { key: 'title', header: 'Round', render: (s) => <strong>{s.title}</strong> },
            {
              key: 'crane',
              header: 'Crane',
              render: (s) => (
                <span className="cell-stack">
                  <span>{s.assetCode || '—'}</span>
                  <span className="muted">{[s.department, s.bay].filter(Boolean).join(' · ')}</span>
                </span>
              ),
            },
            { key: 'interval', header: 'Every', render: (s) => `${s.intervalDays} days` },
            { key: 'lastDone', header: 'Last inspected', render: (s) => fmtDate(s.lastDoneAt) },
            {
              key: 'nextDue',
              header: 'Next due',
              render: (s) => (s.nextDueAt
                ? <span className={s.overdue ? 'val-bad' : undefined}>{fmtDate(s.nextDueAt)}</span>
                : <span className="muted">—</span>),
            },
            {
              key: 'state',
              header: 'State',
              render: (s) => (s.active === false
                ? <StatusPill status="CLOSED" title="Retired — not offered as work" />
                : <StatusPill status="ACTIVE" />),
            },
          ]}
          rows={plans}
          empty={<p className="muted">No rounds scheduled yet.</p>}
        />
      </Card>

      {/* ---- The register ---- */}
      <SectionHeader hint="Click an inspection to read every parameter, or download the signed report">
        Inspection register
      </SectionHeader>
      <Card>
        <Table
          keyOf={(r) => r.id}
          onRowClick={(r) => setOpen(r)}
          columns={[
            {
              key: 'crane',
              header: 'Crane',
              render: (r) => (
                <span className="cell-stack">
                  <span>{r.assetCode || '—'}</span>
                  <span className="muted">{[r.dept || r.department, r.bay].filter(Boolean).join(' · ')}</span>
                </span>
              ),
            },
            { key: 'when', header: 'Inspected', render: (r) => fmtDate(r.inspectedAt) },
            {
              key: 'by',
              header: 'By',
              // The names written on the form, not the account that uploaded it — those differ when a
              // supervisor enters a round two officers walked.
              render: (r) => (r.inspectedBy?.length ? r.inspectedBy.join(', ') : (r.inspectedByName || '—')),
            },
            {
              key: 'findings',
              header: 'Findings',
              render: (r) => {
                if (!r.notOkCount) return <span className="val-good">All OK</span>
                return (
                  <span className={r.criticalNotOkCount ? 'val-bad' : undefined}>
                    {r.notOkCount} Not OK
                    {r.criticalNotOkCount ? ` · ${r.criticalNotOkCount} critical` : ''}
                  </span>
                )
              },
            },
            {
              key: 'report',
              header: 'Report',
              render: (r) => (
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    // The failure is reported in the page banner rather than swallowed: a download that
                    // silently does nothing is indistinguishable from a slow one.
                    downloadReport(r).catch((err) => setNotice({ kind: 'danger', text: err.message }))
                  }}
                >
                  PDF
                </Button>
              ),
            },
          ]}
          rows={rows}
          empty={<p className="muted">No inspections recorded yet.</p>}
        />
      </Card>

      {open && <InspectionDetail inspection={open} onClose={() => setOpen(null)} />}

      {editing && (
        <RoundForm
          schedule={editing === 'new' ? null : editing}
          fleet={fleet}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={(body) => {
            const isNew = editing === 'new'
            run(
              () => api(isNew ? '/inspection-schedules' : `/inspection-schedules/${editing.id}`, {
                method: isNew ? 'POST' : 'PATCH',
                body,
              }),
              isNew ? `Scheduled "${body.title}".` : `"${body.title || editing.title}" updated.`,
            )
          }}
          onDeactivate={() => run(
            () => api(`/inspection-schedules/${editing.id}/deactivate`, { method: 'POST', body: {} }),
            `"${editing.title}" retired.`,
          )}
        />
      )}
    </>
  )
}

/**
 * Fetch the PDF and hand it to the browser.
 *
 * Not a plain `<a href>`: the endpoint needs the bearer token, and an anchor would send an unauthenticated
 * request and save the 401 body as a file named `report.pdf` — the exact "an error page under a document name"
 * failure the Android download check exists to catch.
 *
 * Through `api(..., { raw: true })` rather than a hand-rolled `fetch`, so it inherits the single-flight token
 * refresh. Without that, a report downloaded 31 minutes into a session would 401 once and fail, while every
 * other call on the page silently refreshed and succeeded.
 */
async function downloadReport(inspection) {
  const res = await api(`/inspections/${inspection.id}/report.pdf`, { raw: true })
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `inspection-${inspection.assetCode || 'crane'}-${(inspection.inspectedAt || '').slice(0, 10)}.pdf`
  a.click()
  // Revoked on the next tick rather than immediately: some browsers cancel an in-flight download when the
  // object URL disappears before the click has been dispatched.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/* ---- One inspection, every parameter ------------------------------------- */

function InspectionDetail({ inspection, onClose }) {
  const full = useApi(`/inspections/${inspection.id}`)
  const [downloadError, setDownloadError] = useState(null)
  const items = Array.isArray(full.data?.items) ? full.data.items : []
  const failed = items.filter((i) => i.rating === 'NOT_OK')

  // Grouped by section in the paper form's own order — display order, not alphabetical, so the page reads the
  // way the crane is walked.
  const sections = useMemo(() => {
    const out = []
    items.slice().sort((a, b) => a.displayOrder - b.displayOrder).forEach((i) => {
      const last = out[out.length - 1]
      if (last && last.name === i.section) last.items.push(i)
      else out.push({ name: i.section, items: [i] })
    })
    return out
  }, [items])

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <h2>{inspection.assetCode} · {fmtDate(inspection.inspectedAt)}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          {full.loading && <Loading rows={6} />}
          {full.error && <ErrorState error={full.error} onRetry={full.reload} />}

          {full.data && (
            <>
              {/* Failures first. On paper a reader has to scan sixty Observation cells to find what was wrong,
                  which is the main reason these reports go unread. */}
              {failed.length === 0 ? (
                <Banner kind="success">No parameter was recorded Not OK.</Banner>
              ) : (
                <Banner kind={inspection.criticalNotOkCount ? 'danger' : 'warning'}>
                  <span>
                    <strong>{failed.length} Not OK</strong>
                    {inspection.criticalNotOkCount ? ` — ${inspection.criticalNotOkCount} safety-critical` : ''}
                  </span>
                </Banner>
              )}
              {failed.length > 0 && (
                <ul className="plain-list">
                  {failed.map((i) => (
                    <li key={i.itemKey}>
                      {i.critical && <strong>CRITICAL · </strong>}
                      {i.section} — {i.label}
                      {i.note && <span className="muted"> · {i.note}</span>}
                    </li>
                  ))}
                </ul>
              )}

              <SectionHeader>Inspected by</SectionHeader>
              <p>{full.data.inspectedBy?.length ? full.data.inspectedBy.join(', ') : (full.data.inspectedByName || '—')}</p>
              {full.data.notes && (
                <>
                  <SectionHeader>Remarks</SectionHeader>
                  <p>{full.data.notes}</p>
                </>
              )}

              <SectionHeader>Every parameter</SectionHeader>
              {sections.map((section) => (
                <Card key={section.name}>
                  <SectionHeader>{section.name}</SectionHeader>
                  <Table
                    keyOf={(i) => i.itemKey}
                    columns={[
                      { key: 'label', header: 'Parameter', render: (i) => (i.critical ? <strong>{i.label}</strong> : i.label) },
                      { key: 'rating', header: 'Observation', render: (i) => <StatusPill status={pillFor(i.rating)} title={ratingLabel(i.rating)} /> },
                      { key: 'note', header: 'Note', render: (i) => i.note || <span className="muted">—</span> },
                    ]}
                    rows={section.items}
                  />
                </Card>
              ))}

              {downloadError && <Banner kind="danger">{downloadError}</Banner>}
              <Button
                onClick={() => {
                  setDownloadError(null)
                  downloadReport(inspection).catch((err) => setDownloadError(err.message))
                }}
              >
                Download the signed report
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ---- Schedule a round ---------------------------------------------------- */

function RoundForm({ schedule, fleet, busy, onClose, onSubmit, onDeactivate }) {
  const isNew = !schedule
  const [f, setF] = useState({
    assetId: schedule?.assetId || '',
    title: schedule?.title || 'Monthly safety inspection',
    intervalDays: schedule?.intervalDays ?? 30,
    lastDoneAt: schedule?.lastDoneAt ? schedule.lastDoneAt.slice(0, 10) : '',
  })
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const canSubmit = f.title.trim() && (isNew ? f.assetId : true) && Number(f.intervalDays) > 0

  function submit(e) {
    e.preventDefault()
    // A date input gives YYYY-MM-DD; the server wants an OffsetDateTime. Start-of-day in the browser's zone
    // rather than UTC midnight, which lands on the previous day for a plant at +05:30.
    const lastDone = f.lastDoneAt ? new Date(`${f.lastDoneAt}T00:00:00`).toISOString() : null
    const common = { title: f.title.trim(), intervalDays: Number(f.intervalDays), lastDoneAt: lastDone }
    // assetId is absent from the PATCH deliberately: re-pointing a round at another crane would silently
    // reattribute the inspection history that answered it.
    onSubmit(isNew ? { ...common, assetId: f.assetId } : common)
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="sheet-head">
          <h2>{isNew ? 'Schedule an inspection round' : `Edit ${schedule.title}`}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          <div className="grid-2">
            <Field label="Round" id="it" hint="What the safety officer will see, e.g. 'Monthly safety inspection'">
              <input id="it" value={f.title} onChange={set('title')} required autoFocus />
            </Field>

            <Field
              label="Crane"
              id="ia"
              hint={isNew ? 'Which machine this round is for' : 'Cannot change — retire and schedule again'}
            >
              <select id="ia" value={f.assetId} onChange={set('assetId')} disabled={!isNew} required={isNew}>
                <option value="">Select…</option>
                {fleet
                  .filter((a) => a.status !== 'DECOMMISSIONED')
                  .map((a) => <option key={a.id} value={a.id}>{a.assetCode} — {a.name}</option>)}
              </select>
            </Field>

            <Field label="Every (days)" id="in">
              <input id="in" type="number" min="1" value={f.intervalDays} onChange={set('intervalDays')} required />
            </Field>

            <Field
              label="Last inspected"
              id="il"
              hint="The due date is counted from here. Leave blank and it counts from the crane's last recorded inspection, or today."
            >
              <input id="il" type="date" value={f.lastDoneAt} onChange={set('lastDoneAt')} />
            </Field>
          </div>

          <Banner kind="info">
            Only the safety department is served these rounds. Electrical and mechanical engineers see
            preventive maintenance and never inspections — and safety sees inspections and never PM.
          </Banner>

          {!isNew && schedule.active !== false && (
            <>
              <SectionHeader>Retire</SectionHeader>
              <p className="muted">
                Retiring stops the round being offered and keeps the record. There is no delete: the schedule is
                part of the explanation of why the crane was inspected on the days it was.
              </p>
              <Button type="button" variant="danger" disabled={busy} onClick={onDeactivate}>
                Retire {schedule.title}
              </Button>
            </>
          )}
        </div>

        <footer className="sheet-foot">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || !canSubmit}>
            {busy ? 'Saving…' : isNew ? 'Schedule round' : 'Save changes'}
          </Button>
        </footer>
      </form>
    </div>
  )
}

/** The stored value is a wire enum; a person reads a sentence. */
const ratingLabel = (rating) => ({ OK: 'OK', NOT_OK: 'Not OK', NA: 'Not applicable' }[rating] || rating)

/** Map a rating onto the shared status vocabulary so the pill colour and icon come from one place. */
const pillFor = (rating) => ({ OK: 'RESOLVED', NOT_OK: 'OVERDUE', NA: 'CLOSED' }[rating] || 'NEW')

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * The sample size, and the critical count, beneath the findings chart.
 *
 * A section's bar height is failures; its urgency is whether any of them were safety-critical. Ranking on the
 * first and stating the second keeps the chart honest — two failures on hooks matter more than six on cabin
 * housekeeping, and a chart alone cannot say that.
 */
function findingsNote(rows) {
  const notOk = rows.reduce((a, f) => a + f.notOk, 0)
  if (!notOk) return undefined
  const critical = rows.reduce((a, f) => a + f.critical, 0)
  const inspected = rows.reduce((a, f) => a + f.inspected, 0)
  const parts = [
    notOk < SAMPLE_FLOOR
      ? `${notOk} failure${notOk === 1 ? '' : 's'} in ${inspected} ratings — too few to read as a pattern`
      : `${notOk} failures in ${inspected} ratings`,
  ]
  if (critical > 0) parts.push(`${critical} safety-critical`)
  return parts.join(' · ')
}
